<template>
  <div class="rates-editor">
    <div class="editor-header">
      <div>
        <h3>Gestion des taux</h3>
        <p class="editor-help">
          Modifiez les marges d'achat/vente et la visibilité des devises. Le taux source reste synchronisé par l'API.
        </p>
        <p v-if="errorMessage" class="message error">{{ errorMessage }}</p>
        <p v-if="successMessage" class="message success">{{ successMessage }}</p>
      </div>
      <button type="button" class="btn-refresh" @click="fetchRates">Actualiser</button>
    </div>

    <form class="create-form" @submit.prevent="createRate">
      <input v-model.trim="newRate.currency_code" placeholder="Code" maxlength="10" />
      <input v-model.trim="newRate.currency_name" placeholder="Nom de la devise" />
      <input v-model.trim="newRate.country_name_fr" placeholder="Nom FR" />
      <input v-model.trim="newRate.country_name_en" placeholder="Nom EN" />
      <input v-model.number="newRate.base_rate" type="number" step="0.00000001" min="0" placeholder="Taux source" />
      <input v-model.number="newRate.buy_margin" type="number" step="0.000001" min="0" placeholder="buy_margin" />
      <input v-model.number="newRate.sell_margin" type="number" step="0.000001" min="0" placeholder="sell_margin" />
      <input v-model.number="newRate.display_order" type="number" step="1" placeholder="Ordre" />
      <label class="checkbox-label">
        <input v-model="newRate.active" type="checkbox" />
        Actif
      </label>
      <label v-for="flag in visibilityFlags" :key="'new-' + flag.key" class="checkbox-label">
        <input v-model="newRate[flag.key]" type="checkbox" />
        {{ flag.label }}
      </label>
      <button type="submit" class="btn-add">Ajouter</button>
    </form>

    <p v-if="loading" class="message">Chargement...</p>

    <table class="currency-table">
      <thead>
        <tr>
          <th>Ordre</th>
          <th>Code</th>
          <th>Nom</th>
          <th>Noms admin</th>
          <th>Taux source</th>
          <th>buy_margin</th>
          <th>sell_margin</th>
          <th>Achat</th>
          <th>Vente</th>
          <th>Actif</th>
          <th>Visibilité</th>
          <th>Actions</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="rate in editableRates" :key="rate.id" :class="{ inactive: !rate.active }">
          <td><input v-model.number="rate.display_order" type="number" step="1" /></td>
          <td><input v-model.trim="rate.currency_code" maxlength="10" /></td>
          <td>
            <input v-model.trim="rate.currency_name" />
            <small v-if="isTestRate(rate)" class="test-label">Donnée de test</small>
          </td>
          <td>
            <input v-model.trim="rate.country_name_fr" placeholder="FR" />
            <input v-model.trim="rate.country_name_en" placeholder="EN" />
          </td>
          <td>{{ formatRate(rate.source_rate || rate.base_rate) }}</td>
          <td><input v-model.number="rate.buy_margin" type="number" step="0.000001" min="0" /></td>
          <td><input v-model.number="rate.sell_margin" type="number" step="0.000001" min="0" /></td>
          <td>{{ formatRate(rate.buy_rate) }}</td>
          <td>{{ formatRate(rate.sell_rate) }}</td>
          <td><input v-model="rate.active" type="checkbox" /></td>
          <td class="visibility-cell">
            <label v-for="flag in visibilityFlags" :key="rate.id + '-' + flag.key">
              <input v-model="rate[flag.key]" type="checkbox" />
              {{ flag.label }}
            </label>
          </td>
          <td class="actions">
            <button type="button" class="btn-save" @click="updateRate(rate)">Enregistrer</button>
            <button type="button" class="btn-disable" :disabled="!rate.active" @click="disableRate(rate)">Désactiver</button>
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script>
import axios from 'axios';
import { apiUrl } from '@/config/api';

const blankRate = () => ({
  currency_code: '',
  currency_name: '',
  country_name_fr: '',
  country_name_en: '',
  base_rate: 1,
  buy_margin: 0.03,
  sell_margin: 0.03,
  active: true,
  home: false,
  autre: false,
  principale: true,
  board1: false,
  board2: false,
  display_order: 0
});

export default {
  name: "currenciesEditor",
  props: {
    token: {
      type: String,
      required: true
    },
    setLoading: {
      type: Function,
      required: true
    }
  },
  data() {
    return {
      loading: false,
      errorMessage: '',
      successMessage: '',
      editableRates: [],
      newRate: blankRate(),
      visibilityFlags: [
        { key: 'home', label: 'Home' },
        { key: 'principale', label: 'Principale' },
        { key: 'autre', label: 'Autre' },
        { key: 'board1', label: 'Board 1' },
        { key: 'board2', label: 'Board 2' }
      ]
    };
  },
  async mounted() {
    await this.fetchRates();
  },
  methods: {
    authHeaders() {
      return {
        Authorization: 'Bearer ' + this.token,
        'Content-Type': 'application/json'
      };
    },
    formatRate(value) {
      const number = Number(value);
      if (!Number.isFinite(number)) return '-';
      return number.toFixed(number >= 100 ? 2 : 6);
    },
    isTestRate(rate) {
      return String(rate.currency_name || '').toUpperCase().includes('TEST');
    },
    normalizeRate(rate) {
      return {
        ...rate,
        base_rate: Number(rate.base_rate),
        source_rate: Number(rate.source_rate || rate.base_rate),
        buy_margin: Number(rate.buy_margin),
        sell_margin: Number(rate.sell_margin),
        active: Boolean(rate.active),
        home: Boolean(rate.home),
        autre: Boolean(rate.autre),
        principale: Boolean(rate.principale),
        board1: Boolean(rate.board1),
        board2: Boolean(rate.board2),
        display_order: Number(rate.display_order)
      };
    },
    showSuccess(message) {
      this.successMessage = message;
      this.errorMessage = '';
    },
    showError(message) {
      this.errorMessage = message;
      this.successMessage = '';
    },
    async fetchRates() {
      this.loading = true;
      this.setLoading(true);
      try {
        const response = await axios.get(apiUrl('/admin/rates'), {
          headers: this.authHeaders()
        });
        this.editableRates = (response.data?.rates || [])
          .map(this.normalizeRate)
          .sort((a, b) => a.display_order - b.display_order || a.currency_code.localeCompare(b.currency_code));
      } catch (error) {
        this.showError('Impossible de charger les taux admin.');
        console.error(error);
      } finally {
        this.loading = false;
        this.setLoading(false);
      }
    },
    async createRate() {
      try {
        const payload = {
          ...this.newRate,
          currency_code: String(this.newRate.currency_code || '').toUpperCase(),
          currency_name: this.newRate.currency_name || this.newRate.country_name_fr || this.newRate.country_name_en
        };
        await axios.post(apiUrl('/admin/rates'), payload, {
          headers: this.authHeaders()
        });
        this.newRate = blankRate();
        this.showSuccess('Devise créée.');
        await this.fetchRates();
      } catch (error) {
        this.showError('Impossible de créer cette devise.');
        console.error(error);
      }
    },
    async updateRate(rate) {
      try {
        const payload = {
          currency_code: String(rate.currency_code || '').toUpperCase(),
          currency_name: rate.currency_name,
          country_name_fr: rate.country_name_fr,
          country_name_en: rate.country_name_en,
          buy_margin: Number(rate.buy_margin),
          sell_margin: Number(rate.sell_margin),
          active: Boolean(rate.active),
          home: Boolean(rate.home),
          autre: Boolean(rate.autre),
          principale: Boolean(rate.principale),
          board1: Boolean(rate.board1),
          board2: Boolean(rate.board2),
          display_order: Number(rate.display_order)
        };
        await axios.patch(apiUrl('/admin/rates/' + rate.id), payload, {
          headers: this.authHeaders()
        });
        this.showSuccess('Taux enregistré.');
        await this.fetchRates();
      } catch (error) {
        this.showError('Impossible de modifier ce taux.');
        console.error(error);
      }
    },
    async disableRate(rate) {
      try {
        await axios.delete(apiUrl('/admin/rates/' + rate.id), {
          headers: this.authHeaders()
        });
        this.showSuccess('Devise désactivée.');
        await this.fetchRates();
      } catch (error) {
        this.showError('Impossible de désactiver cette devise.');
        console.error(error);
      }
    }
  }
};
</script>

<style scoped>
.rates-editor {
  padding: 20px;
  background-color: #fff;
  border: 1px solid #d9dee7;
  border-radius: 8px;
  box-shadow: 0 12px 34px rgba(0, 16, 65, 0.08);
  overflow-x: auto;
}

.editor-header,
.create-form {
  display: flex;
  gap: 10px;
  align-items: center;
  flex-wrap: wrap;
  margin-bottom: 18px;
}

.editor-header {
  justify-content: space-between;
}

.editor-help {
  color: #444;
  font-size: 0.95rem;
  margin: 6px 0 0;
}

.create-form input,
.currency-table input {
  background-color: #fff;
  border: 1px solid #c9d0dc;
  border-radius: 5px;
  color: #000;
  padding: 8px;
}

.create-form input {
  min-width: 120px;
}

.currency-table {
  border-spacing: 0;
  width: 100%;
  max-width: none;
}

.currency-table th {
  position: sticky;
  top: 0;
  background: #001041;
  color: #fff;
  font-size: 0.78rem;
  letter-spacing: 0;
  padding: 10px;
  text-transform: uppercase;
}

.currency-table td {
  background: #fff;
  border-bottom: 1px solid #e3e7ef;
  border-left: 0;
  border-right: 0;
  border-top: 0;
  padding: 8px;
}

.currency-table input[type="text"],
.currency-table input[type="number"],
.currency-table input:not([type]) {
  max-width: 140px;
  width: 100%;
}

.inactive td {
  opacity: 0.55;
}

.actions {
  display: flex;
  gap: 8px;
}

.visibility-cell {
  min-width: 150px;
}

.visibility-cell label {
  display: block;
  color: #000;
  font-size: 12px;
  line-height: 1.4;
}

.checkbox-label {
  display: flex;
  gap: 6px;
  align-items: center;
}

button {
  border: 0;
  border-radius: 5px;
  cursor: pointer;
  padding: 9px 12px;
}

.btn-add,
.btn-save,
.btn-refresh {
  background-color: #28a745;
  color: white;
}

.btn-disable {
  background-color: #8b1e1e;
  color: white;
}

.btn-disable:disabled {
  background-color: #999;
  cursor: not-allowed;
}

.message {
  border-radius: 6px;
  margin: 10px 0 0;
  padding: 10px 12px;
}

.error {
  background: #fff0f0;
  border: 1px solid #e2b1b1;
  color: #9b1c1c;
}

.success {
  background: #edf8f0;
  border: 1px solid #b7dec0;
  color: #176b2c;
}

.test-label {
  display: block;
  color: #9b1c1c;
  font-weight: 700;
  margin-top: 4px;
}
</style>
